/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// react
import { useRef, useEffect, useState } from 'react';

// mantine
import { Drawer, Box, ScrollArea } from '@mantine/core';
import { isNotEmpty, useForm } from '@mantine/form';

// Styles
import classes from './AddApiToken.module.css';

// local components
import { PreviousDrawerButton } from '@components/atoms/PreviousDrawerButton';
import { DefaultModal } from '@components/molecules/DefaultModal';
import { AddApiTokenForm } from './AddApiTokenForm/AddApiTokenForm';

// store
import useStore from '@store/index';

// i18n
import { useTranslation } from 'react-i18next';
import { TFormValuesApiToken } from '../../types';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const AddApiToken = (props: {
	onClick?: () => void;
	onClose: () => void;
	opened: boolean;
}) => {
	/* ******************************** STATES ******************************* */
	const [isOpenModalCancel, setIsOpenModalCancel] =
		useState(false);
	/* ******************************** HOOKS *********************************** */
	const viewport = useRef<HTMLDivElement>(null);
	const { t } = useTranslation();

	const {
		offsetCreationActiveStep,
		setOffsetCreationActiveStep,
	} = useStore();

	// Validators
	const form = useForm<TFormValuesApiToken>({
		initialValues: {
			name: '',
			organization: null,
			entity: null,
			expiredAt: null,
			domain: '',
		},
		validate: {
			name: (value) =>
				value.length < 2
					? t('global.nameMincharacter')
					: null,
			organization: isNotEmpty(
				t('global.organizationRequired')
			),

			entity: isNotEmpty(t('global.entityRequired')),
			domain: (value: string) =>
				/^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,61}$|^$/.test(
					value
				) || value == null
					? null
					: t('global.invalidDomain'),
		},
	});

	useEffect(() => {
		if (
			offsetCreationActiveStep >= 0 &&
			viewport &&
			viewport.current
		) {
			viewport.current!.scrollTo({
				top: 0,
				behavior: 'smooth',
			});
		}
	}, [offsetCreationActiveStep]);

	useEffect(() => {
		setOffsetCreationActiveStep(0);
	}, []);

	/* ******************************** RENDERING ******************************* */
	return (
		<Drawer.Root
			position="right"
			size="100%"
			opened={props.opened}
			onClose={() => {
				setIsOpenModalCancel(true);
			}}
		>
			<Drawer.Overlay />
			<Drawer.Content
				ref={viewport}
				className={classes.root}
			>
				<DefaultModal
					opened={isOpenModalCancel}
					onConfirm={() => {
						props.onClose();
						setIsOpenModalCancel(false);
						form.reset();
					}}
					onClose={() => {
						setIsOpenModalCancel(false);
					}}
					title={t('apiTokens.sureToBackToAllApiTokens')}
				/>
				{/** Header */}
				<Drawer.Header className={classes.header}>
					<PreviousDrawerButton
						onClick={() => {
							setIsOpenModalCancel(true);
						}}
					>
						{t('apiTokens.backToAllApiTokens')}
					</PreviousDrawerButton>
				</Drawer.Header>
				{/** Body */}
				<Drawer.Body className={classes.body}>
					<Box className={classes.bodySpacer} />
					<Box className={classes.bodyContent}>
						{/** Content */}
						<ScrollArea
							viewportRef={viewport}
							className={classes.containerContent}
						>
							<AddApiTokenForm
								form={form}
								onCancel={() => {
									setIsOpenModalCancel(true);
								}}
								onBack={() => {
									props.onClose();
									form.reset();
								}}
							/>
						</ScrollArea>
					</Box>
					<Box className={classes.bodySpacer} />
				</Drawer.Body>
				{/** Footer */}
				<Box className={classes.containerFooter}>
					<Box className={classes.spacerFooter} />
					<Box className={classes.spacerFooter} />
				</Box>
			</Drawer.Content>
		</Drawer.Root>
	);
};
